import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';

const DryFruits = () => {
  const categories = [
    {
      id: 'cashews',
      name: 'Cashew Nuts',
      image: 'https://images.unsplash.com/photo-1756361946161-1cb809eae6cb?w=800',
      description: 'Premium cashew kernels in whole grades (W180–W450) and ingredient grades (Splits, Butts, LWP, SWP, BB) for retail, HoReCa, and manufacturing.',
      link: '/products/dry-fruits/cashews',
      highlights: [
        '10 core SKUs covering retail and ingredient needs',
        'Standard Export & Premium EU/USA-Ready grades',
        'Batch COA and lot-coded traceability',
        'Vacuum/nitrogen flush packing available'
      ]
    },
    {
      id: 'almonds',
      name: 'Almonds',
      image: 'https://static.prod-images.emergentagent.com/jobs/2a82a0ed-6235-40ba-a9f3-10baa6fa26b5/images/bc0fb0d9c602b35a89ce7477b364282e71e964411c16256fe5ed7ec650ffcd76.png',
      description: 'Premium California and Mediterranean almonds in whole, sliced, slivered, and blanched forms for snacking, bakery, and confectionery.',
      link: '/products/dry-fruits/almonds',
      highlights: [
        'Multiple grades: NPS, Carmel, Nonpareil',
        'Whole, sliced, slivered, diced, blanched options',
        'Retail and bulk packaging available',
        'Destination-aligned specifications'
      ]
    },
    {
      id: 'walnuts',
      name: 'Walnuts',
      image: 'https://static.prod-images.emergentagent.com/jobs/2a82a0ed-6235-40ba-a9f3-10baa6fa26b5/images/9613e5729ed3d67c1a9ef55c72645997a50dceb5bcaa92958b33a167e32d4c1f.png',
      description: 'Premium walnut halves and pieces from California and Chile for bakery, snacks, and ingredient manufacturing.',
      link: '/products/dry-fruits/walnuts',
      highlights: [
        'Light halves & pieces (LHP), quarters available',
        'Multiple color grades: Extra Light, Light, Amber',
        'Low moisture for extended shelf life',
        'Batch COA with every shipment'
      ]
    },
    {
      id: 'pistachios',
      name: 'Pistachios',
      image: 'https://static.prod-images.emergentagent.com/jobs/2a82a0ed-6235-40ba-a9f3-10baa6fa26b5/images/ab2dc43240e588bb1d2657600f16cf08a6f730265817575a805f72b5a4465aa0.png',
      description: 'Premium in-shell and kernel pistachios from Iran, USA, and Turkey for snacking, bakery, and ice cream manufacturing.',
      link: '/products/dry-fruits/pistachios',
      highlights: [
        'In-shell roasted/salted and raw kernels',
        'Green peeled kernels for confectionery',
        'Multiple sizes and grades available',
        'Premium packaging for retail programs'
      ]
    },
    {
      id: 'raisins',
      name: 'Raisins',
      image: 'https://static.prod-images.emergentagent.com/jobs/2a82a0ed-6235-40ba-a9f3-10baa6fa26b5/images/79f7cbbe7d0eca54a949edc15ad34d6c14272d345fbb9f7c16711be7e768536d.png',
      description: 'Premium golden and dark raisins for bakery, confectionery, cereal, and snack applications.',
      link: '/products/dry-fruits/raisins',
      highlights: [
        'Golden, Sultana, Thompson Seedless varieties',
        'Multiple origins: India, Turkey, USA',
        'Bulk and retail packaging options',
        'Oil-coated and natural options'
      ]
    },
    {
      id: 'figs',
      name: 'Dried Figs',
      image: 'https://static.prod-images.emergentagent.com/jobs/2a82a0ed-6235-40ba-a9f3-10baa6fa26b5/images/6a65171c3eab624fe936cff9abb4f7f111d16dcb655e60b7778007b5ee945644.png',
      description: 'Premium Turkish and Mediterranean dried figs for retail, HoReCa, and ingredient applications.',
      link: '/products/dry-fruits/figs',
      highlights: [
        'Lerida, Garland, and Calimyrna varieties',
        'Multiple sizes: Jumbo, Extra, Choice',
        'Natural sun-dried processing',
        'Long shelf life with proper storage'
      ]
    }
  ];

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link to="/products" className="inline-flex items-center text-[#263776] hover:text-[#F47D20] mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Products
            </Link>
            <div className="text-center">
              <h1 className="text-5xl font-bold mb-4">Dry Fruits</h1>
              <p className="text-xl text-slate-700">
                Premium cashews, almonds, and assorted dry fruits for retail, HoReCa, and ingredient manufacturing
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto space-y-16">
            {categories.map((category, index) => (
              <div key={category.id} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center`}>
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  {category.link ? (
                    <Link to={category.link}>
                      <img
                        src={category.image}
                        alt={category.name}
                        className="rounded-lg shadow-lg w-full object-cover h-80 hover:shadow-2xl transition-shadow cursor-pointer"
                      />
                    </Link>
                  ) : (
                    <img
                      src={category.image}
                      alt={category.name}
                      className="rounded-lg shadow-lg w-full object-cover h-80"
                    />
                  )}
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  {category.link ? (
                    <Link to={category.link}>
                      <h2 className="text-3xl font-bold text-[#263776] mb-4 hover:text-[#F47D20] transition-colors cursor-pointer">
                        {category.name}
                      </h2>
                    </Link>
                  ) : (
                    <h2 className="text-3xl font-bold text-[#263776] mb-4">{category.name}</h2>
                  )}
                  <p className="text-lg text-slate-700 mb-6">{category.description}</p>
                  
                  <ul className="space-y-3 mb-6">
                    {category.highlights.map((highlight, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle2 className="w-5 h-5 text-[#F47D20] mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{highlight}</span>
                      </li>
                    ))}
                  </ul>

                  <Button asChild className="bg-[#263776] text-white hover:bg-[#1e2a5e]">
                    <Link to={category.link || '/contact'}>
                      {category.link ? `Explore ${category.name}` : `Inquire About ${category.name}`}
                    </Link>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Standards */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-8">Quality & Documentation Standards</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="p-6 text-center">
                <h3 className="font-semibold text-lg mb-2 text-[#263776]">Batch COA</h3>
                <p className="text-sm text-slate-600">Certificate of Analysis supplied with every shipment</p>
              </Card>
              <Card className="p-6 text-center">
                <h3 className="font-semibold text-lg mb-2 text-[#263776]">Lot-Coded Traceability</h3>
                <p className="text-sm text-slate-600">Full traceability via production/date codes</p>
              </Card>
              <Card className="p-6 text-center">
                <h3 className="font-semibold text-lg mb-2 text-[#263776]">Destination-Aligned</h3>
                <p className="text-sm text-slate-600">Specs aligned to UAE, UK-EU, USA, Africa requirements</p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Other Dry Fruits Link */}
      <section className="py-16 bg-white border-t">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4 text-[#263776]">Looking for More Options?</h2>
            <p className="text-lg text-slate-700 mb-6">
              Explore our extended range including dates, apricots, prunes, cranberries, hazelnuts, and more.
            </p>
            <Button asChild size="lg" className="bg-[#F47D20] text-white hover:bg-[#d66b1a]">
              <Link to="/products/dry-fruits/other">
                View Other Dry Fruits & Nuts
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#263776] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Source Premium Dry Fruits?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Share your requirements—grade, quantity, destination, and pack type. We'll provide a detailed quote with documentation checklist.
          </p>
          <div className="flex gap-4 justify-center">
            <Button asChild size="lg" className="bg-[#F47D20] text-white hover:bg-[#d66b1a]">
              <Link to="/contact">Request a Quote</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white bg-white/10 hover:bg-white/20">
              <Link to="/products">View All Products</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DryFruits;
